import React, { useEffect, useState } from 'react';
import { HStack, Image, VStack, Box } from 'native-base';
import { Caption } from '~/shared/Typography';
import { QuantityPicker } from '~/shared/QuantityPicker';
import { PriceTag } from '~/pages/shop/components/PriceTag';
import {
    Item,
    useDutchieStore,
    useRemoveItemFromCartMutation, useTheme,
    useUpdateQuantityCartMutation,
} from '@springbig/shared';
import { useToast } from '~/hooks/toast';
import { Card } from '~/shared/Theme';

export type CartItemProps = {
    item: Item;
};

export const CartItemCard = ({ item }: CartItemProps) => {

    const {
        primary,
        radius_md,
        background_secondary,
    } = useTheme();

    const _baseSize = 120;
    const [quantity, setQuantity] = useState(item.quantity);
    const variant =
        item.product.variants.filter((e) => e.option === item.option)[0];

    const {
        setCheckout,
        retailer,
        checkout,
        setIsBusy,
        isBusy,
        menuType
    } = useDutchieStore();

    const {
        mutate: removeItem,
    } = useRemoveItemFromCartMutation();

    const {
        mutate: updateQuantity,
        data: newQuantity,
        isError: isUpdateQuantityError,
        error: updateQuantityError,
        isLoading: isLoadingUpdateQuantity,
    } = useUpdateQuantityCartMutation();

    const { errorToast } = useToast();

    useEffect(() => {

        setIsBusy(isLoadingUpdateQuantity);
    }, [isLoadingUpdateQuantity]);

    useEffect(() => {

        if (newQuantity && !isUpdateQuantityError) {
            setCheckout(newQuantity.updateQuantity);
            const value : Item = newQuantity.updateQuantity.items.filter((e) => e.id === item.id)[0];
            if (value) {
                setQuantity(value?.quantity);
            }
        }
    }, [newQuantity, updateQuantityError]);

    const setQuantityValues = (value: number): void => {

        if (value === 0) {
            removeItem({
                retailerId: retailer.id,
                checkoutId: checkout.id,
                itemId: item.id
            }, {
                onSuccess: (newRemoveItem) => {

                    setCheckout(newRemoveItem.removeItem);
                    const _value: Item = newRemoveItem.removeItem.items.filter((e) => e.id === item.id)[0];
                    if (_value) {
                        setQuantity(value?.quantity);
                    }
                },
                onError: (error) => {

                    if (error.data.errors[0].message === 'Not Found') {
                        const newCheckout = checkout;
                        if (newCheckout) {
                            newCheckout.items = newCheckout.items.filter((itemElment) => itemElment.id !== item.id);
                            setCheckout(newCheckout);
                        }
                    }
                }
            });
        }
        else if (value < variant.quantity) {
            updateQuantity({
                retailerId: retailer.id,
                checkoutId: checkout.id,
                itemId: item.id,
                quantity: value,
            }, {
                onError: (error) => {

                    errorToast(error.data.errors[0].message);
                }
            });
        }
    };

    return (
        <Card row justifyContent={'space-between'} flex={1} mb={4} p={0}>
            <VStack
                px={4}
                my={2}
                space={1}
                justifyContent={'space-between'}
                flex={1}
            >
                <Caption
                    fontSize={14}
                    numberOfLines={2}
                    textOverflow={'ellipsis'}
                    w={'100%'}
                >
                    { item.product.name }
                </Caption>
                <HStack space={16} alignItems={'baseline'}>
                    <PriceTag
                        price={menuType === 'MEDICAL' ?
                            variant.priceMed
                            :
                            variant.priceRec
                        }
                        discountedPrice={ menuType === 'MEDICAL' ?
                            variant.specialPriceMed
                            :
                            variant.specialPriceRec
                        }
                    />
                    { item.option !== 'N/A' &&
                        <Caption fontSize={12}>
                            { item.option }
                        </Caption>
                    }
                </HStack>
                <Box alignSelf={'flex-start'}>
                    <QuantityPicker
                        quantity={quantity}
                        maxQuantity={variant.quantity}
                        isLoading={isBusy}
                        setQuantity={(value) => setQuantityValues(value)}
                        variant={'small'}
                    />
                </Box>
            </VStack>
            <Box
                bg={background_secondary}
                rounded={radius_md}
                borderColor={primary}
                borderWidth={1} 
                my={'auto'}
            >
                <Image
                    src={item.product.images[0].url + `?w=${_baseSize}&h=${_baseSize}`}
                    w={`${_baseSize}px`}
                    h={`${_baseSize}px`}
                    rounded={radius_md}
                />
            </Box>
        </Card>
    );
};
